import { useRateConCountdown } from "@/management/hooks/useRateConCountdown"
import { defaultTimezone, IExecutionStrategySnapshotDto, StrategyType } from "@/shared/types"
import { getSnapshotStrategyType, isActiveRateConSnapshot, strategyToText } from "@/shared/utils"

import { Copy, Flex, Stack } from "@/shared/components"

import { RateConStatusBadge } from "./RateConStatusBadge"

interface IRateConSnapshotRowProps {
  snapshot: IExecutionStrategySnapshotDto
  timeZone?: string
}

export const RateConSnapshotRow = ({ snapshot, timeZone = defaultTimezone }: IRateConSnapshotRowProps) => {
  const isActive = isActiveRateConSnapshot(snapshot)
  const countdown = useRateConCountdown(
    isActive ? snapshot.rateConConfirmationEndTime ?? undefined : undefined,
    timeZone,
  )

  const isWaitingForRequest = snapshot.status === "WAITING_FOR_RATE_CON_REQUEST"
  const isPendingConfirmation =
    snapshot.status === "RATECON_PENDING_CONFIRMATION" && snapshot.rateConConfirmationStatus === "PENDING"
  const isDeclinedOrExpired =
    snapshot.rateConConfirmationStatus === "DECLINED" || snapshot.rateConConfirmationStatus === "EXPIRED"

  return (
    <Stack space={4}>
      <Flex align="center" wrap css={{ gap: "$12" }}>
        <Copy scale={10} color="theme-b-n3" fontWeight="bold">
          {strategyToText(getSnapshotStrategyType(snapshot) as StrategyType)}
        </Copy>
        <RateConStatusBadge
          status={snapshot.status}
          confirmationStatus={snapshot.rateConConfirmationStatus ?? undefined}
        />
      </Flex>

      {isWaitingForRequest ? (
        <Copy scale={10} color="theme-n6-n5">
          Rate confirmation pending — external system will request it
        </Copy>
      ) : null}

      {isPendingConfirmation && countdown ? (
        <Stack space={4}>
          <Copy scale={10} color="theme-b-n3" fontWeight="bold">
            {countdown.isExpired ? "Confirmation window expired" : countdown.countdownLabel}
          </Copy>
          <Copy scale={10} color="theme-n6-n5">
            {`Expires at ${countdown.expiresAtLabel}`}
          </Copy>
        </Stack>
      ) : null}

      {isDeclinedOrExpired ? (
        <Copy scale={10} color="special-error">
          {snapshot.rateConConfirmationStatus === "DECLINED"
            ? "Carrier declined — moved to next strategy"
            : "No response in time — moved to next strategy"}
        </Copy>
      ) : null}
    </Stack>
  )
}
